import { useEffect, useState } from "react";
import type { SettingsResponse } from "../../shared/types";
import { DEFAULT_IDLE_MINUTES } from "../../shared/tags";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { looksLikeEmail } from "../setup/rules";
import type { Run } from "../setup/useSettingsData";
import { automationsBody, idleMinutesFrom, teamOf } from "./automations";

/** The minutes and the team as being changed: kept until Save, which sends both as the automations save does. */
export function useAutomationsDraft(data: SettingsResponse, run: Run) {
  const toast = useToast();
  const savedMins = data.automations.idle_minutes ?? DEFAULT_IDLE_MINUTES;
  const savedTeam = teamOf(data);
  const [mins, setMins] = useState(String(savedMins));
  const [team, setTeam] = useState(savedTeam);
  const [adding, setAdding] = useState("");
  const [saving, setSaving] = useState(false);
  const savedKey = `${savedMins}|${savedTeam.join(",")}`;
  // A save brings the draft back to what is stored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMins(String(savedMins));
    setTeam(savedTeam);
  }, [savedKey]);

  const dirty = idleMinutesFrom(mins) !== savedMins || team.join(",") !== savedTeam.join(",");
  const add = () => {
    const v = adding.trim();
    if (!looksLikeEmail(v)) return toast({ text: "That doesn’t look like an email address.", icon: "info" });
    setAdding("");
    setTeam((t) => (t.includes(v) ? t : [...t, v]));
  };
  const save = async () => {
    setSaving(true);
    const minutes = idleMinutesFrom(mins);
    setMins(String(minutes));
    await run(() => api.settings.saveAutomations(automationsBody(minutes, team)), "Saved");
    setSaving(false);
  };
  return {
    mins,
    setMins,
    team,
    adding,
    setAdding,
    dirty,
    saving,
    add,
    remove: (e: string) => setTeam((t) => t.filter((x) => x !== e)),
    save,
    discard: () => {
      setMins(String(savedMins));
      setTeam(savedTeam);
      setAdding("");
    },
  };
}
